import { useState, useEffect, useCallback } from 'react';
import { adminGetUsers, adminToggleUser, adminCreateUser, adminUpdateUser, adminDeleteUser, changeUserRole } from '../../utils/api';
import { formatDate } from '../../utils/helpers';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { FiSearch, FiX, FiToggleLeft, FiToggleRight, FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import styles from './AdminTable.module.css';

const EMPTY = { name:'', email:'', phone:'', password:'', role:'user' };

const RC = {
  admin:     { label:'Admin',     emoji:'👑', bg:'#dbeafe', color:'#1e40af' },
  moderator: { label:'Moderator', emoji:'🛡️', bg:'#d1fae5', color:'#065f46' },
  editor:    { label:'Editor',    emoji:'✏️', bg:'#fef3c7', color:'#92400e' },
  user:      { label:'User',      emoji:'👤', bg:'#f3f4f6', color:'#374151' },
};
const ROLE_DESC = {
  user:      'Can browse and purchase products.',
  editor:    'Can create/edit products & categories.',
  moderator: 'Editor permissions + manage orders & coupons.',
  admin:     'Full system access.',
};

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers]         = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [modal, setModal]         = useState(null); // 'create' | 'edit'
  const [form, setForm]           = useState(EMPTY);
  const [editUser, setEditUser]   = useState(null);
  const [saving, setSaving]       = useState(false);
  const [showPass, setShowPass]   = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const p = { page, limit:15 };
      if (search)     p.search = search;
      if (roleFilter) p.role   = roleFilter;
      const { data } = await adminGetUsers(p);
      setUsers(data.users); setTotal(data.total);
    } finally { setLoading(false); }
  }, [page, search, roleFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openCreate = () => { setForm(EMPTY); setEditUser(null); setModal('create'); };
  const openEdit   = u  => {
    setForm({ name:u.name, email:u.email, phone:u.phone||'', password:'', role:u.role });
    setEditUser(u); setModal('edit');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (modal==='create' && form.password.length < 6) { toast.error('Password min 6 chars'); return; }
    setSaving(true);
    try {
      if (modal==='edit') {
        const payload = { name:form.name, email:form.email, phone:form.phone, role:form.role };
        if (form.password.length >= 6) payload.password = form.password;
        await adminUpdateUser(editUser._id, payload);
        toast.success('✅ User updated!');
      } else {
        await adminCreateUser(form);
        toast.success(`✅ User "${form.name}" created!`);
      }
      setModal(null); fetchUsers();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Permanently delete "${name}"? This cannot be undone.`)) return;
    try { await adminDeleteUser(id); toast.success('User deleted'); fetchUsers(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleToggle = async (id, name, isActive) => {
    if (!window.confirm(`${isActive?'Deactivate':'Activate'} "${name}"?`)) return;
    try { await adminToggleUser(id); toast.success(isActive ? 'Deactivated' : 'Activated'); fetchUsers(); }
    catch { toast.error('Failed'); }
  };

  const handleRoleChange = async (id, newRole, name) => {
    if (!window.confirm(`Change "${name}" role to ${newRole}?`)) return;
    try { await changeUserRole(id, newRole); toast.success(`Role → ${newRole}`); fetchUsers(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const f = field => e => setForm(p => ({ ...p, [field]: e.target.value }));
  const pages = Math.ceil(total / 15);

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h2 className={styles.pageTitle}>Users</h2>
          <p className={styles.pageSubtitle}>{total} registered users</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}><FiPlus size={15}/> Create User</button>
      </div>

      {/* Role filter */}
      <div style={{ display:'flex', gap:7, flexWrap:'wrap' }}>
        {['','admin','moderator','editor','user'].map(r => {
          const rc = r ? RC[r] : null;
          return (
            <button key={r} onClick={() => { setRoleFilter(r); setPage(1); }} style={{
              padding:'5px 14px', borderRadius:999, fontSize:'.76rem', fontWeight:700, cursor:'pointer',
              border:`1.5px solid`, borderColor: roleFilter===r?(rc?.color||'var(--primary)'):'var(--border)',
              background: roleFilter===r?(rc?.bg||'var(--primary-light)'):'#fff',
              color: roleFilter===r?(rc?.color||'var(--primary)'):'var(--text-secondary)',
            }}>
              {r ? `${rc.emoji} ${rc.label}` : '👥 All'}
            </button>
          );
        })}
      </div>

      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <FiSearch size={15} className={styles.searchIcon}/>
          <input className={styles.searchInput} placeholder="Search name or email…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}/>
          {search && <button className={styles.clearSearch} onClick={() => setSearch('')}><FiX size={13}/></button>}
        </div>
      </div>

      <div className={styles.tableCard}>
        {loading ? <div className="spinner-center"><div className="spinner"/></div> : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr><th>User</th><th>Phone</th><th>Role</th><th>Status</th><th>Joined</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {users.length === 0
                  ? <tr><td colSpan={6} className={styles.emptyRow}>No users found</td></tr>
                  : users.map(u => {
                  const rc = RC[u.role] || RC.user;
                  const isMe = u._id === me?._id;
                  return (
                    <tr key={u._id}>
                      <td>
                        <div className={styles.productCell}>
                          <div style={{ width:36,height:36,borderRadius:'50%',flexShrink:0,background:rc.bg,color:rc.color,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:800,fontSize:'.875rem',border:`1.5px solid ${rc.color}40` }}>
                            {u.name?.[0]?.toUpperCase()}
                          </div>
                          <div>
                            <p className={styles.productName}>{u.name} {isMe && <span style={{ fontSize:'.68rem',color:'var(--primary)',fontWeight:700 }}>(you)</span>}</p>
                            <p className={styles.productSub}>{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td style={{ fontSize:'.83rem' }}>{u.phone||'—'}</td>
                      <td>
                        {isMe ? (
                          <span style={{ display:'inline-flex',alignItems:'center',gap:4,background:rc.bg,color:rc.color,padding:'3px 10px',borderRadius:999,fontSize:'.74rem',fontWeight:700 }}>{rc.emoji} {rc.label}</span>
                        ) : (
                          <select value={u.role} onChange={e => handleRoleChange(u._id, e.target.value, u.name)} style={{ padding:'4px 26px 4px 8px',borderRadius:8,cursor:'pointer',border:`1.5px solid ${rc.color}50`,background:rc.bg,color:rc.color,fontSize:'.74rem',fontWeight:700,outline:'none',appearance:'none',backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,backgroundRepeat:'no-repeat',backgroundPosition:'right 7px center' }}>
                            {Object.entries(RC).map(([r,v]) => <option key={r} value={r}>{v.emoji} {v.label}</option>)}
                          </select>
                        )}
                      </td>
                      <td><span className={`badge ${u.isActive?'badge-success':'badge-error'}`}>{u.isActive?'● Active':'● Inactive'}</span></td>
                      <td style={{ fontSize:'.76rem',color:'var(--text-muted)' }}>{formatDate(u.createdAt)}</td>
                      <td>
                        <div className={styles.actionBtns}>
                          <button className="btn btn-ghost btn-sm" onClick={() => openEdit(u)} title="Edit"><FiEdit2 size={13}/></button>
                          {!isMe && <>
                            <button className="btn btn-ghost btn-sm" style={{ color:u.isActive?'var(--warning)':'var(--success)' }} onClick={() => handleToggle(u._id,u.name,u.isActive)} title={u.isActive?'Deactivate':'Activate'}>
                              {u.isActive ? <FiToggleRight size={15}/> : <FiToggleLeft size={15}/>}
                            </button>
                            <button className="btn btn-ghost btn-sm" style={{ color:'var(--error)' }} onClick={() => handleDelete(u._id,u.name)} title="Delete permanently"><FiTrash2 size={13}/></button>
                          </>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {pages > 1 && (
          <div className="pagination">
            {Array.from({ length:pages },(_,i)=>i+1).map(p=>(
              <button key={p} className={`page-btn ${p===page?'active':''}`} onClick={()=>setPage(p)}>{p}</button>
            ))}
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {modal && (
        <div className={styles.modalOverlay} onClick={() => setModal(null)}>
          <div className={styles.modal} style={{ maxWidth:500 }} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>{modal==='create' ? '➕ Create User' : `✏️ Edit — ${editUser?.name}`}</h3>
              <button className={styles.modalClose} onClick={() => setModal(null)}><FiX size={20}/></button>
            </div>
            <form onSubmit={handleSubmit} className={styles.modalForm}>
              <div style={{ display:'flex', flexDirection:'column', gap:'.875rem' }}>
                <div className="form-group">
                  <label className="form-label">Full Name *</label>
                  <input className="form-input" value={form.name} onChange={f('name')} placeholder="Rahim Uddin" required/>
                </div>
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input type="email" className="form-input" value={form.email} onChange={f('email')} placeholder="user@example.com" required/>
                </div>
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input type="tel" className="form-input" value={form.phone} onChange={f('phone')} placeholder="01XXXXXXXXX"/>
                </div>
                <div className="form-group">
                  <label className="form-label">{modal==='edit' ? 'New Password (leave blank to keep current)' : 'Password * (min 6 chars)'}</label>
                  <div style={{ position:'relative' }}>
                    <input type={showPass?'text':'password'} className="form-input" value={form.password} onChange={f('password')} placeholder="••••••••" minLength={modal==='create'?6:0} required={modal==='create'} style={{ paddingRight:52 }}/>
                    <button type="button" onClick={() => setShowPass(s=>!s)} style={{ position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',fontSize:'.72rem',color:'var(--text-muted)',fontWeight:700 }}>{showPass?'HIDE':'SHOW'}</button>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Role *</label>
                  <select className="form-input form-select" value={form.role} onChange={f('role')} disabled={editUser?._id === me?._id}>
                    {Object.entries(RC).map(([r,v]) => <option key={r} value={r}>{v.emoji} {v.label}</option>)}
                  </select>
                </div>
                <div style={{ background:RC[form.role]?.bg, color:RC[form.role]?.color, border:`1.5px solid ${RC[form.role]?.color}40`, padding:'9px 13px', borderRadius:10, fontSize:'.8rem', display:'flex', gap:8 }}>
                  <span style={{ fontSize:'1rem' }}>{RC[form.role]?.emoji}</span>
                  <span>{ROLE_DESC[form.role]}</span>
                </div>
              </div>
              <div className={styles.modalFooter}>
                <button type="button" className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? <><div className="spinner spinner-sm"/> Saving…</> : modal==='create' ? '✅ Create User' : '✅ Update User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
